// Grouped search — pure, DOM- and state-free (SEARCH_SPEC §2).
//
// Every match is grouped under its parent dataset ("main" row, depth 0) with any
// matching sub-series nested beneath it (depth 1). An empty query lists the
// featured "Popular series" first, then a "Maps" block (the map-only entries),
// then every other dataset under an "All datasets" divider. All inputs
// (catalog, sub-index, featured ids) are passed in, so the same function serves
// the landing bar, the detail bar, and the results page, and is unit-testable
// without the DOM.
//
// Ranking follows the server's /resolve scoring (api/discover.R,
// ds_field_token / ds_resolve_score):
//
// - Text is compared case- and accent-free, across all languages, with
//   punctuation treated as a word break.
// - Per query word, each field scores its weight times how well the word hit:
//   a whole word 1, the start of a word 0.75, the middle of a word 0.3 ("rent"
//   inside "current"). The word's score is its best field. A plural "s"/"es"
//   still counts as the whole word, so "franc" hits "francs" fully and
//   "France" only as a word start. A plural query word is also tried in the
//   singular: "Mieten" finds "Miete", "rates" finds "rate".
// - Field weights: dataset title (and featured name, and the datasheet's
//   keywords: "CPI", "franc", "EUR") 3, sub-series label 2.5,
//   a map's words for its series 2, concept/topic 1.2, source/id 0.8, the
//   dataset's description, units and other dims' levels 0.8, a
//   sub-series' ancestor path 1. A sub-series also gets 0.4 of its parent
//   dataset's score for a word, so "cpi tobacco" finds the CPI's "Tobacco".
// - Every word has to hit somewhere. Function words ("by", "the", "der") are
//   dropped first, unless the query is nothing else.
// - Bonuses: the whole query equal to a title (or its plural) +3 (a sub-series label +0.4),
//   a multi-word query found as a phrase in a title or label +1.5.
// - A sub-series is listed only if a query word hit its own label or path;
//   one that matched only through its parent adds nothing to the parent row.
// - Groups (a dataset with its sub-series, or a map-only entry) are ordered by
//   their best score, then by the shorter title, then by catalog order.
//   Sub-series within a group by score, shorter label, index order.

export interface CatalogItem {
  id: string;
  title?: any;
  source?: string;
  topic?: string;
  concept?: string;
  frequency?: string;
  [k: string]: any;
}
export interface SubSeries {
  datasetId: string;
  datasetTitle?: string;
  dim: string;
  code: string;
  label: string;
  path: string[];
  freq?: string;
}

/** One map of maps.dataseries.org (GET /maps). With `series`, it is the same
 *  statistic as those datasets: search finds them by the map's words, and the
 *  series page links to it. Without, it is a map-only row. */
export interface MapItem {
  id: string;
  url: string;
  title?: any;
  source?: any;
  topic?: any;
  frequency?: string;
  levels_label?: any;
  start?: string;
  end?: string;
  series?: string[];
  /** Subhead in the topic list (maps.json 1.1). */
  concept?: any;
  /** Picture of the map, an absolute URL (maps.json 1.1). */
  thumbnail?: string;
}

export interface SearchRow {
  depth: 0 | 1;
  isSub: boolean;
  id: string;
  title: string;
  badge: string;
  freq: string;
  hasSubMatch?: boolean;
  restStart?: boolean;
  subdim?: string;
  subcode?: string;
  /** Map URL of a map-only row. */
  map?: string;
  /** What the map shows, e.g. "Cantons · monthly, Municipalities · annual". */
  mapLevels?: string;
  mapOnly?: boolean;
  /** First row of the "Maps" block (empty query). */
  mapsStart?: boolean;
}

export interface SearchCtx {
  catalog: CatalogItem[];
  subIndex: SubSeries[];
  subReady: boolean;
  /** Featured dataset ids, in display order (the "Popular series" block). */
  featuredIds: string[];
  label: (t: any) => string;
  badgeOf: (id: string) => string;
  maps?: MapItem[];
  /** GET /search-words.json: per dataset id, the words beyond its title. */
  words?: Record<string, DatasetWords>;
}

/** What a dataset answers to besides its title (ds_search_words in api/query.R):
 *  "exports to Germany" names a level of the flow dim, "franc" only appears in
 *  the exchange-rate description. */
export interface DatasetWords {
  /** The datasheet's search words, scored like the title. */
  keywords?: unknown;
  description?: unknown;
  units?: unknown;
  levels?: unknown[];
}

/** The map that shows a dataset's statistic, if any. */
export const mapForSeries = (maps: MapItem[] | undefined, id: string): MapItem | undefined =>
  (maps || []).find(m => (m.series || []).includes(id));

// ---- text normalisation -----------------------------------------------------

/** Lower-case, accent-free, punctuation as single spaces: "Chômage (BIT)" ->
 *  "chomage bit". Query and text go through the same function. */
export const foldText = (s: string): string =>
  s.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/** Every string inside a field, at any depth: a bare string, a label object
 *  {en, de, …}, or a nested one like concept {en, leaf: {…}} / topic {key, name}. */
const strings = (x: unknown): string => {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number") return String(x);
  if (Array.isArray(x)) return x.map(strings).join(" ");
  if (typeof x === "object") return Object.values(x as Record<string, unknown>).map(strings).join(" ");
  return "";
};

/** Each string of a field on its own, folded: the exact-title check compares
 *  the query with one language at a time. */
const variants = (x: unknown): string[] => {
  if (x == null) return [];
  if (typeof x === "string") return [foldText(x)];
  if (Array.isArray(x)) return x.flatMap(variants);
  if (typeof x === "object") return Object.values(x as Record<string, unknown>).flatMap(variants);
  return [];
};

/** A searchable field: folded text padded with spaces, so " word" is a word
 *  start and " word " a whole word. */
const field = (x: unknown): string => {
  const f = foldText(strings(x));
  return f ? ` ${f} ` : "";
};

// Same list as DS_RESOLVE_STOPWORDS in api/discover.R, folded (für -> fur).
const STOPWORDS = new Set([
  // en
  "a", "an", "and", "are", "as", "at", "be", "by", "for", "from", "how", "in",
  "is", "it", "of", "on", "or", "that", "the", "to", "what", "which", "with",
  // de
  "auf", "das", "dem", "den", "der", "des", "die", "ein", "eine", "einen",
  "fur", "ist", "mit", "nach", "sind", "und", "von", "wie",
  // fr
  "dans", "de", "du", "en", "est", "et", "la", "le", "les", "par", "pour",
  "sont", "sur", "un", "une",
  // it
  "come", "con", "dei", "della", "delle", "gli", "il", "lo", "nel", "quale",
]);

/** Query words, folded, function words dropped (unless that drops them all). */
export const queryTokens = (query: string): string[] => {
  const all = foldText(query || "").split(" ").filter(Boolean);
  const kept = all.filter(t => !STOPWORDS.has(t));
  return kept.length ? kept : all;
};

// ---- scoring -----------------------------------------------------------------

const WHOLE = 1, PREFIX = 0.75, MID = 0.3;
const W_TITLE = 3, W_MAP = 2, W_DESC = 1.2, W_SRC = 0.8, W_ABOUT = 0.8;
const W_LABEL = 2.5, W_PATH = 1, W_PARENT = 0.4;
const EXACT_TITLE = 3, EXACT_LABEL = 0.4, PHRASE = 1.5, CANONICAL = 0.3;

/** How well one word form hits one field: whole word (or its plural), word
 *  start, mid-word, or 0. */
const formLevel = (f: string, t: string): number => {
  if (!f.includes(` ${t}`)) return f.includes(t) ? MID : 0;
  return f.includes(` ${t} `) || f.includes(` ${t}s `) || f.includes(` ${t}es `) ? WHOLE : PREFIX;
};

/** A query word and, if it looks plural (en/fr -s, -es; de -n, -en), its
 *  singulars, never shorter than four letters. */
const wordForms = (t: string): string[] => {
  const forms = [t];
  for (const suf of ["es", "en", "s", "n"])
    if (t.endsWith(suf) && t.length - suf.length >= 4) forms.push(t.slice(0, -suf.length));
  return forms;
};

/** How well one query word hits one field: the best of its forms. */
const hitLevel = (f: string, forms: string[]): number => {
  if (!f) return 0;
  let best = 0;
  for (const t of forms) best = Math.max(best, formLevel(f, t));
  return best;
};

interface DsFields { title: string; titles: string[]; desc: string; src: string; canonical: boolean }
interface SubFields { label: string; labels: string[]; path: string; ptitle: string }

// Folded fields are cached per object: the catalog and the sub-index are
// long-lived, and search runs on every keystroke.
const dsCache = new WeakMap<object, DsFields>();
const subCache = new WeakMap<object, SubFields>();
const mapCache = new WeakMap<object, { title: string; titles: string[]; desc: string }>();
const wordsCache = new WeakMap<object, { about: string; keywords: string }>();

const wordsFields = (w: DatasetWords | undefined): { about: string; keywords: string } => {
  if (!w) return { about: "", keywords: "" };
  let f = wordsCache.get(w);
  if (!f) {
    f = { about: field([w.description, w.units, w.levels]), keywords: field(w.keywords) };
    wordsCache.set(w, f);
  }
  return f;
};

const dsFields = (d: CatalogItem): DsFields => {
  let f = dsCache.get(d);
  if (!f) {
    const src = typeof d.source === "string" ? d.source : (d.source as any)?.name;
    f = {
      title: field([d.title, d.featured]),
      titles: variants([d.title, d.featured]),
      desc: field([d.concept, (d.topic as any)?.name ?? d.topic]),
      src: field([src, d.id]),
      canonical: !!d.canonical,
    };
    dsCache.set(d, f);
  }
  return f;
};
const subFields = (s: SubSeries): SubFields => {
  let f = subCache.get(s);
  if (!f) {
    f = { label: field(s.label), labels: variants(s.label), path: field(s.path), ptitle: field(s.datasetTitle) };
    subCache.set(s, f);
  }
  return f;
};
const mapFields = (m: MapItem) => {
  let f = mapCache.get(m);
  if (!f) {
    f = { title: field(m.title), titles: variants(m.title), desc: field([m.source?.name, m.topic?.name, m.levels_label]) };
    mapCache.set(m, f);
  }
  return f;
};

/** Sum of per-word scores, or 0 if any word missed. `wordScore` gives one
 *  word's best field score. */
const allWords = (words: string[][], wordScore: (t: string[]) => number): number => {
  let sum = 0;
  for (const t of words) {
    const s = wordScore(t);
    if (s <= 0) return 0;
    sum += s;
  }
  return sum;
};

// ---- search -----------------------------------------------------------------

interface Group {
  key: string;          // dataset id, or map id for a map-only row
  order: number;        // catalog order; maps after the catalog
  score: number;
  len: number;          // display-title length, the brevity tie-break
  subs: { s: SubSeries; score: number; len: number; order: number }[];
  map?: MapItem;
}

export function searchGrouped(query: string, excludeId: string | null, ctx: SearchCtx): SearchRow[] {
  const { catalog, subIndex, subReady, featuredIds, label, badgeOf } = ctx;
  const maps = ctx.maps || [];
  const q = (query || "").trim();
  const notSelf = (id: string) => id !== excludeId;
  const byId = new Map(catalog.map(c => [c.id, c] as const));
  const mainRow = (id: string, hasSubMatch: boolean): SearchRow => {
    const c = byId.get(id) || ({} as CatalogItem);
    return { depth: 0, isSub: false, id, title: label(c.title) || id, badge: badgeOf(id), freq: c.frequency || "", hasSubMatch };
  };
  const mapOnly = maps.filter(m => !(m.series || []).length);
  const mapRow = (m: MapItem): SearchRow => ({
    depth: 0, isSub: false, id: m.id, title: label(m.title) || m.id,
    badge: String(m.source?.key || "").toUpperCase(), freq: m.frequency || "",
    map: m.url, mapLevels: label(m.levels_label), mapOnly: true,
  });

  if (!q) {
    const featIds = featuredIds.filter(id => notSelf(id) && byId.has(id));
    const featRows = featIds.map(id => mainRow(id, false));
    const mapRows = mapOnly.map(mapRow);
    if (mapRows.length) mapRows[0]!.mapsStart = true;
    const rest = catalog.filter(c => notSelf(c.id) && featIds.indexOf(c.id) < 0);
    const restRows = rest.map((c, i) => { const r = mainRow(c.id, false); if (i === 0) r.restStart = true; return r; });
    return [...featRows, ...mapRows, ...restRows];
  }

  const tokens = queryTokens(q);
  if (!tokens.length) return [];
  const phrase = tokens.join(" ");
  const multi = tokens.length > 1;
  const words = tokens.map(wordForms);
  const exact = (vs: string[]) => vs.includes(phrase) || vs.includes(`${phrase}s`) || vs.includes(`${phrase}es`);

  // A map with series lends its words to those datasets (a German "Einwohner"
  // finds Resident population).
  const mapText: Record<string, string> = {};
  for (const m of maps) for (const id of m.series || []) {
    const mf = mapFields(m);
    mapText[id] = (mapText[id] || "") + mf.title + mf.desc;
  }

  // One word against one dataset: its best field.
  const dsWord = (f: DsFields, mapF: string, w: { about: string; keywords: string }) => (t: string[]) => Math.max(
    W_TITLE * hitLevel(f.title, t), W_TITLE * hitLevel(w.keywords, t), W_MAP * hitLevel(mapF, t),
    W_DESC * hitLevel(f.desc, t), W_SRC * hitLevel(f.src, t), W_ABOUT * hitLevel(w.about, t));

  const groups = new Map<string, Group>();
  const dsScoreCache = new Map<string, (t: string[]) => number>();
  catalog.forEach((d, i) => {
    const f = dsFields(d);
    const wf = wordsFields(ctx.words?.[d.id]);
    const word = dsWord(f, mapText[d.id] || "", wf);
    dsScoreCache.set(d.id, word);
    let score = allWords(words, word);
    if (score > 0) {
      if (exact(f.titles)) score += EXACT_TITLE;
      if (multi && f.title.includes(` ${phrase}`)) score += PHRASE;
      if (f.canonical) score += CANONICAL;
    }
    groups.set(d.id, { key: d.id, order: i, score, len: (label(d.title) || d.id).length, subs: [] });
  });

  if (subReady) subIndex.forEach((s, i) => {
    const f = subFields(s);
    const parent = dsScoreCache.get(s.datasetId);
    let own = false;   // did any word hit the sub-series' own label/path?
    let score = allWords(words, t => {
      const l = W_LABEL * hitLevel(f.label, t), p = W_PATH * hitLevel(f.path, t);
      if (l > 0 || p > 0) own = true;
      const up = parent ? parent(t) : W_TITLE * hitLevel(f.ptitle, t);
      return Math.max(l, p, W_PARENT * up);
    });
    if (score <= 0 || !own) return;
    if (exact(f.labels)) score += EXACT_LABEL;
    if (multi && f.label.includes(` ${phrase}`)) score += PHRASE;
    let g = groups.get(s.datasetId);
    if (!g) {
      g = { key: s.datasetId, order: catalog.length + maps.length + i, score: 0,
            len: (label(s.datasetTitle) || s.datasetId).length, subs: [] };
      groups.set(s.datasetId, g);
    }
    g.subs.push({ s, score, len: label(s.label).length, order: i });
  });

  maps.forEach((m, i) => {
    if ((m.series || []).length) return;   // scored through its series above
    const f = mapFields(m);
    let score = allWords(words, t => Math.max(W_TITLE * hitLevel(f.title, t), W_DESC * hitLevel(f.desc, t)));
    if (score <= 0) return;
    if (exact(f.titles)) score += EXACT_TITLE;
    if (multi && f.title.includes(` ${phrase}`)) score += PHRASE;
    groups.set(`map:${m.id}`, { key: m.id, order: catalog.length + i, score, len: (label(m.title) || m.id).length, subs: [], map: m });
  });

  const ranked: Group[] = [];
  for (const g of groups.values()) {
    if (!g.map && !notSelf(g.key)) continue;   // never compare a series with itself / its own breakdowns
    g.subs.sort((a, b) => b.score - a.score || a.len - b.len || a.order - b.order);
    g.score = Math.max(g.score, g.subs[0]?.score ?? 0);
    if (g.score > 0) ranked.push(g);
  }
  ranked.sort((a, b) => b.score - a.score || a.len - b.len || a.order - b.order);

  const rows: SearchRow[] = [];
  for (const g of ranked) {
    if (g.map) { rows.push(mapRow(g.map)); continue; }
    rows.push(mainRow(g.key, g.subs.length > 0));
    for (const { s } of g.subs.slice(0, 8))
      rows.push({ depth: 1, isSub: true, id: s.datasetId, subdim: s.dim, subcode: s.code, title: label(s.label as any), badge: badgeOf(s.datasetId), freq: s.freq || "" });
  }
  return rows;
}
